import type { IPoll } from '@polls/service/typings';
import type { IMessage } from '@ui/components/organisms/message/message.typings';

export type TPollCacheEndpoint =
  | 'getMessagesOfThread'
  | 'getMessageById'
  | 'getReplyNavigationDetail';

export interface IPollCacheEntry {
  endpointName: TPollCacheEndpoint;
  originalArgs: unknown;
}

type TCacheQuery = { endpointName?: string; originalArgs?: unknown; data?: unknown };

export type TPollCacheState = { queries?: Record<string, TCacheQuery | undefined> } | undefined;

export type TPollRecipe = (poll: IPoll | null | undefined) => IPoll | null | undefined;

type TThreadCache = {
  messages?: { entities?: Record<string, IMessage | undefined> };
  pinnedMessages?: IMessage[];
};

const holdsMessage = (message: IMessage | undefined, messageId: string) =>
  message?.id === messageId ||
  message?.main_post?.id === messageId ||
  message?.parent_post?.id === messageId;

/** Lists every cache entry of the message slice that holds a copy of the message. */
export function findPollCacheEntries(state: TPollCacheState, messageId: string): IPollCacheEntry[] {
  const entries: IPollCacheEntry[] = [];
  Object.values(state?.queries ?? {}).forEach((query) => {
    if (!query?.data) return;
    if (query.endpointName === 'getMessagesOfThread') {
      const data = query.data as TThreadCache;
      const holds =
        Boolean(data.messages?.entities?.[messageId]) ||
        Boolean(data.pinnedMessages?.some((message) => message.id === messageId));
      if (holds)
        entries.push({ endpointName: 'getMessagesOfThread', originalArgs: query.originalArgs });
    } else if (
      query.endpointName === 'getMessageById' ||
      query.endpointName === 'getReplyNavigationDetail'
    ) {
      if (holdsMessage(query.data as IMessage, messageId)) {
        entries.push({ endpointName: query.endpointName, originalArgs: query.originalArgs });
      }
    }
  });
  return entries;
}

const writePoll = (message: IMessage | undefined, recipe: TPollRecipe) => {
  if (!message) return;
  const current = message.poll;
  const next = recipe(current);
  if (next !== current) message.poll = next;
};

/** Runs the recipe on every copy of the message inside one cache draft. */
export function applyPollRecipe(
  endpointName: TPollCacheEndpoint,
  draft: unknown,
  messageId: string,
  recipe: TPollRecipe,
) {
  if (!draft) return;
  if (endpointName === 'getMessagesOfThread') {
    const thread = draft as TThreadCache;
    writePoll(thread.messages?.entities?.[messageId], recipe);
    thread.pinnedMessages?.forEach((message) => {
      if (message.id === messageId) writePoll(message, recipe);
    });
    return;
  }
  const message = draft as IMessage;
  if (message.id === messageId) writePoll(message, recipe);
  if (message.main_post?.id === messageId) writePoll(message.main_post, recipe);
  if (message.parent_post?.id === messageId) writePoll(message.parent_post, recipe);
}

/** Drops a `poll` key a caller spread into an update, so a PATCH never carries it. */
export function withoutPoll<T extends object>(value: T): Omit<T, 'poll'> {
  const { poll: _poll, ...rest } = value as T & { poll?: unknown };
  return rest as Omit<T, 'poll'>;
}
